import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from "react-redux";
import { setConfig } from "../../Redux/Action";
import { StatusBall } from "../../../../../Components/Elements/StatusBall";
import { Typography, Tooltip, Chip } from "@mui/material";
import useDeepEqualSelector from "../../../../../Hooks/useDeepEqualSelector";
import dayjs from "dayjs";

export const JobsTable = ({ isLoading, setIsLoading }) => {
    const dispatch = useDispatch();
    const filters = useSelector((store) => store.rosterJobs?.config?.filters);
    const items = useSelector((store) => 
        filters?.view === 'failed' ? store.rosterJobs.failedList : store.rosterJobs.list
    );
    const config = useDeepEqualSelector((store) => store.rosterJobs.config);

    const columns = React.useMemo(
        () => {
            const baseColumns = [
                {
                    field: 'id',
                    headerName: 'ID',
                    type: 'string',
                    width: 80,
                },
                {
                    field: 'job_name',
                    headerName: 'Job Name',
                    type: 'string',
                    flex: 1,
                    minWidth: 200,
                    renderCell: (params) => (
                        <Typography sx={{ fontSize: '0.86rem', lineHeight: 'inherit' }}>
                            {params.value || 'Unknown Job'}
                        </Typography>
                    )
                },
                {
                    field: 'queue',
                    headerName: 'Queue',
                    type: 'string',
                    width: 120,
                    renderCell: (params) => (
                        <Chip 
                            label={params.value || 'default'} 
                            size="small" 
                            variant="outlined"
                        />
                    )
                },
            ];

            if (filters?.view === 'failed') {
                return [
                    ...baseColumns,
                    {
                        field: 'failed_at_formatted',
                        headerName: 'Failed At',
                        type: 'string',
                        flex: 1,
                        minWidth: 180,
                        renderCell: (params) => {
                            return (
                                <Tooltip title={params.value} arrow>
                                    <Typography sx={{ fontSize: '0.86rem', lineHeight: 'inherit' }}>
                                        {dayjs(params.value).format('DD MMM YYYY hh:mm A')}
                                    </Typography>
                                </Tooltip>
                            );
                        },
                    },
                    {
                        field: 'error_preview',
                        headerName: 'Error',
                        type: 'string',
                        flex: 1,
                        minWidth: 300,
                        renderCell: (params) => (
                            <Tooltip title={params.row.exception || 'No error details'} arrow>
                                <Typography 
                                    sx={{ 
                                        fontSize: '0.86rem', 
                                        lineHeight: 'inherit',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {params.value || 'No error details'}
                                </Typography>
                            </Tooltip>
                        )
                    },
                ];
            } else {
                return [
                    ...baseColumns,
                    {
                        field: 'status',
                        headerName: 'Status',
                        type: 'string',
                        width: 120,
                        renderCell: (params) => {
                            const statusMap = {
                                'pending': { label: 'Pending', color: 'warning' },
                                'processing': { label: 'Processing', color: 'info' },
                            };
                            const status = statusMap[params.value] || { label: 'Unknown', color: 'default' };
                            return <StatusBall label={status.label} size="small" color={status.color} />;
                        }
                    },
                    {
                        field: 'attempts',
                        headerName: 'Attempts',
                        type: 'number',
                        width: 100,
                    },
                    {
                        field: 'created_at_formatted',
                        headerName: 'Created At',
                        type: 'string',
                        flex: 1,
                        minWidth: 180,
                        renderCell: (params) => {
                            return (
                                <Tooltip title={params.value} arrow>
                                    <Typography sx={{ fontSize: '0.86rem', lineHeight: 'inherit' }}>
                                        {dayjs(params.value).format('DD MMM YYYY hh:mm A')}
                                    </Typography>
                                </Tooltip>
                            );
                        },
                    },
                ];
            }
        },
        [filters?.view],
    );

    return (
        <DataGrid
            loading={isLoading}
            rows={items || []}
            getRowId={(row) => row.id}
            rowHeight={60}
            columns={columns}
            pagination
            paginationMode="server"
            paginationModel={config.pagination}
            rowCount={config?.total ?? 0}
            pageSizeOptions={[5, 10, 25, 100]}
            onPaginationModelChange={(params) => !isLoading && (setIsLoading(true) || dispatch(setConfig({ pagination: { ...config.pagination, page: params.page, pageSize: params.pageSize } })))}
            initialState={{
                columns: {
                    columnVisibilityModel: {
                        id: filters?.view !== 'failed',
                    }
                },
                sorting: {
                    sortModel: [{ field: 'id', sort: 'desc' }],
                },
                pagination: { paginationModel: { ...config.pagination } }
            }}
        />
    );
};

